import React, { FC, useState, useEffect, useRef, JSX } from 'react';
import { getClinics } from '../api/getClinics';
import { CareWrapper } from './CareCards';
import {
    PrimaryCareCard,
    SameDayCareCard,
    VirtualCard,
} from './CareCards/CareRecommendations';
import { Carousel } from '../jsxElements';
import { ClinicDetailDropdownCard } from './CareCards/ClinicDetailCard';
import { useAccordionControl, useConfig } from '../hooks';
import { BrandParentUrl, ClinicInfo } from '../types';
import { logException } from '../logging';
import styles from './Clinics.module.css';
import GenericCarousel from './GenericCarousel';
import clinicConfig from '../resources/clinicConfig.json';

import { AmpFlagSet } from '../amplitude';

const GROUP_SIZE = 4;

type ClinicsAttachmentPage = 'menu' | 'retail' | 'urgentCare';

const fetchClinics = async (
    clinicType: string,
    clinicRequestInfo: ClinicsAttachmentData,
    ehrServiceEndpoint: string,
    brandParentUrl: BrandParentUrl
) => {
    try {
        return await getClinics(
            clinicType,
            clinicRequestInfo,
            ehrServiceEndpoint,
            brandParentUrl
        );
    } catch (error: any) {
        logException('fetchClinics error', { exception: error });
        console.log(`Failed to fetch ${clinicType} clinics`);
    }
};

export interface ClinicsAttachmentData {
    appointments: string[];
    brand: string;
    careOptions: string[];
    isUnified?: boolean;
    hasPcp?: boolean;
    primaryCareUrl: string;
    removeBubble: string;
    returnText: string;
    template?: any;
    urgentCareUrl?: string;
    virtualUrl: string;
    waitTime: string;
    zip: string;
}

const CareOptions: FC<{
    data: ClinicsAttachmentData;
    expressCare: boolean;
    featureFlags: AmpFlagSet;
    retailClinics: ClinicInfo[];
    setWhichPageOpen: React.Dispatch<
        React.SetStateAction<ClinicsAttachmentPage>
    >;
    urgentCare: boolean;
    urgentCareClinics: ClinicInfo[];
    virtual: boolean;
    virtualTriage: boolean;
}> = ({
    data,
    expressCare,
    featureFlags,
    urgentCare,
    virtual,
    virtualTriage,
}) => {
    const careElements: JSX.Element[] = [];

    // Conditionally add the virtual card
    if (virtual && data.template) {
        careElements.push(
            <VirtualCard
                featureFlags={featureFlags}
                template={data.template}
                virtualUrl={data.virtualUrl}
                triage={virtualTriage ? true : false}
            />
        );
    }

    // Conditionally add the same day care card
    let showSameDayCareCard = expressCare || urgentCare;
    // We need the urgent care url on mobile or we shouldn't show the card at all
    if (data.isUnified && !data.urgentCareUrl) {
        showSameDayCareCard = false;
    }
    if (showSameDayCareCard) {
        careElements.push(
            <SameDayCareCard data={data} featureFlags={featureFlags} />
        );
    }

    // Add the primary care card
    const primaryCareCard = (
        <PrimaryCareCard data={data} featureFlags={featureFlags} />
    );
    if (data.hasPcp) {
        // If the user has a PCP, we want to show the primary care card first
        careElements.unshift(primaryCareCard);
    } else {
        // If the user doesn't have a PCP, we want to show the primary care card last
        careElements.push(primaryCareCard);
    }

    return <GenericCarousel elements={careElements} />;
};

export const ClinicsAttachment: FC<{
    data: ClinicsAttachmentData;
    disableButtons: boolean;
    featureFlags: AmpFlagSet;
    sendMessage: (message: string) => void;
    sendPostBack: (message: string) => void;
}> = ({ data, disableButtons, featureFlags, sendMessage }) => {
    const { ehrServiceEndpoint, brandParentUrl } = useConfig();
    const [retailClinics, setRetailClinics] = useState<Array<ClinicInfo>>([]);
    const [urgentCareClinics, setUrgentCareClinics] = useState<
        Array<ClinicInfo>
    >([]);
    const [loading, setLoading] = useState({
        retailLoading: true,
        urgentCareLoading: true,
    });

    const [whichPageOpen, setWhichPageOpen] =
        useState<ClinicsAttachmentPage>('menu');

    const [accordionToExpand, clickAccordionOfIndex] = useAccordionControl();

    const navRef = useRef<HTMLDivElement | null>(null);

    const returnText = data.returnText || clinicConfig.providence.returnButton;
    const expressCare = data.careOptions?.includes('EXPRESS_CARE');
    const urgentCare = data.careOptions?.includes('URGENT_CARE');
    const virtual =
        data.careOptions?.includes('VIRTUAL_VISIT') ||
        data.careOptions?.includes('VIRTUAL_VISIT_TRIAGE');
    const virtualTriage = data.careOptions?.includes('VIRTUAL_VISIT_TRIAGE');

    let virtualClinic: ClinicInfo;

    if (virtual) {
        virtualClinic = {
            displayName: clinicConfig.providence.expresscareVirtual,
            address: {
                line1: '',
                line2: '',
                city: '',
                state: '',
                postalCode: '',
            },
            distance: 0,
            phone: '',
            url: data.virtualUrl || '',
            waitTime: '',
            isActive: true,
            clinicType: 'Virtual',
            openDays: [
                {
                    day: 'Sunday',
                    openHours: {
                        startTime: '08:00',
                        endTime: '20:00',
                    },
                },
                {
                    day: 'Saturday',
                    openHours: {
                        startTime: '08:00',
                        endTime: '20:00',
                    },
                },
            ],
            template: data.template,
        };
    }

    useEffect(() => {
        fetchClinics('Retail', data, ehrServiceEndpoint, brandParentUrl).then(
            (r) => {
                let clinicsWithVirtual: ClinicInfo[] = [];
                if (virtual) clinicsWithVirtual.push(virtualClinic);
                if (r)
                    clinicsWithVirtual = clinicsWithVirtual.concat(
                        r as ClinicInfo[]
                    );
                setRetailClinics(clinicsWithVirtual);
                setLoading((prev) => ({ ...prev, retailLoading: false }));
            }
        );

        fetchClinics(
            'UrgentCare',
            data,
            ehrServiceEndpoint,
            brandParentUrl
        ).then((r) => {
            setUrgentCareClinics((r as ClinicInfo[]) || []);
            setLoading((prev) => ({ ...prev, urgentCareLoading: false }));
        });
    }, []);

    useEffect(() => {
        if (
            navRef &&
            navRef.current &&
            !loading.retailLoading &&
            !loading.urgentCareLoading
        ) {
            navRef.current.scrollIntoView({
                behavior: 'smooth',
            });
        }
    }, [loading]);

    let clinicsComponentsArray: JSX.Element[] = [];
    const clinicsArray =
        whichPageOpen === 'retail'
            ? retailClinics
            : whichPageOpen === 'urgentCare'
              ? urgentCareClinics
              : [];

    clinicsComponentsArray = clinicsArray.map((clinic, i) => (
        <div onClick={() => clickAccordionOfIndex(i)}>
            <ClinicDetailDropdownCard
                clinicInfo={clinic}
                expanded={accordionToExpand === `panel${i}`}
                featureFlags={featureFlags}
                virtualWait={data.waitTime}
            />
        </div>
    ));

    const content =
        whichPageOpen === 'menu' ? (
            <div className={styles.slideInFromLeft}>
                <CareOptions
                    data={data}
                    expressCare={expressCare}
                    featureFlags={featureFlags}
                    retailClinics={retailClinics}
                    setWhichPageOpen={setWhichPageOpen}
                    urgentCare={urgentCare}
                    urgentCareClinics={urgentCareClinics}
                    virtual={virtual}
                    virtualTriage={virtualTriage}
                />
            </div>
        ) : (
            <div className={styles.slideInFromRight}>
                <Carousel
                    caller={'clinics'}
                    numItemsToShow={GROUP_SIZE}
                    returnToMenuFunction={() => setWhichPageOpen('menu')}
                >
                    {clinicsComponentsArray}
                </Carousel>
            </div>
        );

    return (
        <div className="clinicsAttachment">
            <CareWrapper
                cardContent={content}
                disableButtons={disableButtons}
                featureFlags={featureFlags}
                loading={loading.retailLoading || loading.urgentCareLoading}
                returnText={returnText}
                returnDescription={clinicConfig.providence.returnDescription}
                sendMessage={sendMessage}
            />
        </div>
    );
};

export default ClinicsAttachment;
